import React, { useEffect, useState } from "react"
import _ from "lodash"
import Isvg from "react-inlinesvg"
// Components
import RowAddRemove from "components/table/carousel/row-add-remove"
import { DateInput, DropdownInput, ListboxInput, NumberInput } from "components/table/carousel/inputs"
import { field_attrs } from "components/table/lib/column-field"
import { WithTip } from 'components/tooltip/with-tip'
// Data
import { checkDateSet } from "data/lib/dates"
import automationService from "data/automation"
// Icons
import { CloseIcon } from "images/icons/general/"
// Styles
import { Button_s, H4 } from "styles/app-styles"
import { FormError_s, SectionFormWell } from "styles/form-styles"
import { MultiRowCarouselState_s, SingleRowCarouselState_s } from "components/table/carousel/carousel-styles"
import { Cap_s, CapButton_s, CapButtons_s } from "components/market-lease/market-lease-styles"

type Item_t = object & {
  i: number
}

type PropTypes = {
  rowData: []
  field: string
  defaults: {}
  namedItems:object
  persist: Function
  title: string
  table: string
}

const RecoveriesProRata = (props: PropTypes) => {
  const initialItems                = _.get(props.rowData[props.field], "prorataRecoveryItems")
  const [capItem, setCap]           = useState<number>(0)
  const [showCap, setShowCap]       = useState<boolean>(false)
  const [homogenous, setHomogenous] = useState<boolean>(true);

  const emptyItem = {
    areaMeasureNamedItemId: props.defaults['recoveryProRataDenominator']['areaNamedItemId'],
    proRataPercent: null,
    proRataType: props.defaults['recoveryProRataProRataType'],
    stopType: props.defaults['recoveryProRataStopType']
  }
  const emptyCapItem = {
    date: null,
    amt: null
  }

  const [items, setItems] = useState<Item_t[]>(initialItems ? _.sortBy(initialItems.map((item, i) => { return {...item, i }})) : [{...emptyItem, i:0}])
  const [capItems, setCapItems] =  useState<Item_t[]>(initialItems ? _.sortBy(initialItems['cap']?.['details']?.map((item, i) => { return {...item, i }})) : [{...emptyCapItem, i:0}])

  useEffect(() => {
    const capItems = items[capItem]['cap']?.['details']?.map((item, i) => { return {...item, i }}) || [{...emptyCapItem, i:0}]
    setCapItems(capItems);
  }, [capItem])

  function updateItems(nextItems:any):void {
    setItems(nextItems)
    nextItems    = nextItems.map(({i, ...rest}) => rest)
    let nextData = _.cloneDeep(props.rowData[props.field]) || {}
    if (nextItems.length === 1 && _.isEqual(emptyItem, nextItems[0])){
      if (nextData['prorataRecoveryItems']) delete nextData['prorataRecoveryItems']
    } else {
      nextData = {...nextData, 'prorataRecoveryItems': nextItems}
    }
    props.persist(nextData, props.field, props.rowData)
  }

  function updateData(val:any, field:string[]):void {
    let nextItems = _.cloneDeep(items);
    if (field[2]){
      // Denominator (area measure)
      nextItems[field[0]][field[1]][field[2]] = val
    } else {
      nextItems[field[0]][field[1]] = val
    }
    // Apply defaults
    if (!nextItems[field[0]]['proRataType']) nextItems[field[0]]['proRataType'] = props.defaults['recoveryProRataProRataType']
    if (!nextItems[field[0]]['stopType']) nextItems[field[0]]['stopType'] = props.defaults['recoveryProRataStopType']
    if (!nextItems[field[0]]['areaMeasureNamedItemId']) nextItems[field[0]]['areaMeasureNamedItemId'] = props.defaults['recoveryProRataDenominator']['areaNamedItemId']
    // Null out Pro Rata and Stop values based on their types
    if (nextItems[field[0]]['proRataType'] === 'Natural') nextItems[field[0]]['proRataPercent'] = null
    if (nextItems[field[0]]['proRataType'] === 'Specified %') delete nextItems[field[0]]['areaMeasureNamedItemId']
    if (nextItems[field[0]]['stopType'] === 'None' ||
        nextItems[field[0]]['stopType'] === 'Occupancy Yr' ||
        nextItems[field[0]]['stopType'] === 'Occupancy Yr+1' ||
        nextItems[field[0]]['stopType'] === 'Occupancy Yr-1') nextItems[field[0]]['stop'] = null

    updateItems(nextItems)
  }

  function updateCap(val:any, field):void {
    let nextItems = _.cloneDeep(items);
    if (!nextItems[capItem]['cap']) nextItems[capItem]['cap'] = { [field]: val }
    else nextItems[capItem]['cap'][field] = val
    if (field === 'capType' && val.startsWith('Detailed')){
      delete nextItems[capItem]['cap']['value']
      delete nextItems[capItem]['cap']['increasePercent']
    }
    if (field === 'capType' && val === 'Percent'){
      delete nextItems[capItem]['cap']['increasePercent']
    }
    if (field === 'capType' && !val.startsWith('Detailed') && nextItems[capItem]['cap']['details']){
      delete nextItems[capItem]['cap']['details']
      if (nextItems[capItem]['dateMode']) delete nextItems[capItem]['cap']['dateMode']
    }
    updateItems(nextItems)
  }

  function updateCapItems(nextCapItems:any):void {
    const strippedCapItems = nextCapItems.map(({i, ...rest}) => rest)
    let nextItems = _.cloneDeep(items);
    nextItems[capItem]['cap']['details'] = strippedCapItems
    updateItems(nextItems)
  }

  function updateCapItem(val:any, field):void {
    let nextCapItems = _.cloneDeep(capItems);
    nextCapItems[field[0]][field[1]] = val
    nextCapItems = nextCapItems.map((item, i) => { return {...item, i }})
    setCapItems(nextCapItems)
    let nextItems = _.cloneDeep(items);
    if (field[1] === 'date'){
      let dateModeCheck = checkDateSet(val, nextCapItems)
      setHomogenous(dateModeCheck.homogenous);
      nextItems[capItem]['cap']['dateMode'] = dateModeCheck.dateMode
    }
    const strippedCapItems = nextCapItems.map(({i, ...rest}) => rest)
    nextItems[capItem]['cap']['details'] = strippedCapItems
    updateItems(nextItems)
  }

  function clearCap():void {
    setItems([{...emptyItem, i:0}])
    setCapItems([{...emptyCapItem, i:0}])
    let nextItems = _.cloneDeep(items);
    delete nextItems[capItem]['cap']
    updateItems(nextItems);
  }

  const toggleAndShowCap = (e:{target:object}, row:object) => {
    // open question as to why clickSync in carousel-automation doesnt trigger when the button is clicked
    automationService.broadcast("FOCUS_DOM_NODE", e.target)
    setCap(row['i'])
    setShowCap(true)
  }

  useEffect(() => {
    // jump react to give FOCUS_DOM_NODE enough time to run, or else this wont offset to the correct node
    if(showCap === true) setTimeout(() => automationService.broadcast("OFFSET_FOCUS", 1), 0)
  }, [showCap])

  const reportTagOptions = props.namedItems?.['reportTags']?.map(tag => {
    if (tag.code) return {label: tag.code, value: tag.reporttagid}
  })
  const recoveryPoolOptions = props.namedItems?.['recoveryPools']?.map(pool => {
    if (pool.code) return {label: pool.code, value: pool.recoverypoolid}
  })
  const areaMeasureOptions = props.namedItems?.['areaMeasures']?.map(area => {
    if (area.code) return {label: area.code, value: area.areaid}
  })

  let renderItems = _.sortBy(items, 'i')
  let renderCapItems = _.sortBy(capItems, 'i')

  const item_attrs = (field) => field_attrs(props.table, `${props.field}.prorataRecoveryItems`)(field)(props.rowData, props)
  const cap_item_attrs = (ix) => (field) => {
    return field_attrs(props.table, `${props.field}.prorataRecoveryItems.${ix}.cap`)(field)(props.rowData, props)
  }

  const params = {
    attrs: item_attrs,
    data: items,
    persist: updateData
  }
  const capParams = (ix) => ({
    attrs: cap_item_attrs(ix),
    data: items[capItem]?.['cap'] || {},
    persist: updateCap
  })
  const capItemParams = (ix) => ({
    attrs: cap_item_attrs(ix),
    data: items[capItem]?.['cap']?.['details'] || {},
    persist: updateCapItem
  })

  return (
    <div className="pro-rata-container">
      <div className="pro-rata-form">
        { renderItems.map((row:object, i) => (
            <MultiRowCarouselState_s className="carousel-multi-row recoveries">
              <div>
              { i === 0 && <label>Recovery Pool</label> }
              {/* <WithTip position="bottom">
                <span>{_.get(_.find(recoveryPoolOptions, (o) => o.value === _.get(row, 'recoveryPoolNamedItemId')), "label")}</span> */}
                <DropdownInput
                  className="automated recoverypool automation_hook_source_row_start"
                  field={[row['i'], 'recoveryPoolNamedItemId']}
                  options={recoveryPoolOptions}
                  {...params}
                />
              {/* </WithTip> */}
              </div>
              <div>
                { i === 0 && <label>Pro Rata Type</label> }
                <DropdownInput
                  className="automated pro-rata-type"
                  field={[row['i'], 'proRataType']}
                  options={['Natural', 'Specified %']}
                  default="Natural"
                  {...params}
                />
              </div>
              <div>
              { i === 0 && <label>Denominator</label> }
                <DropdownInput
                  className="automated"
                  disabled={row['proRataType'] && row['proRataType'] !== "Natural"}
                  disabledText="N/A"
                  field={[row['i'], 'areaMeasureNamedItemId']}
                  options={areaMeasureOptions}
                  {...params}
                />
              </div>
              <div>
                { i === 0 && <label>Pro Rata %</label> }
                <NumberInput
                  className="automated pro-rata-percent"
                  disabled={!row['proRataType'] || row['proRataType'] === "Natural"}
                  disabledText="N/A"
                  field={[row['i'], 'proRataPercent']}
                  min={0}
                  max={100}
                  readPrecision={4}
                  writePrecision={4}
                  {...params}
                />
              </div>
              <div>
                { i === 0 && <label>Stop Type</label> }
                <DropdownInput
                  className="automated stop-type"
                  field={[row['i'], 'stopType']}
                  options={['None', 'Amt', 'Amt/Area', 'Occupancy Yr', 'Occupancy Yr+1', 'Occupancy Yr-1', 'Specified Yr', 'Est. Base Yr']}
                  default="None"
                  {...params}
                />
              </div>
              <div>
                { i === 0 && <label>Stop</label> }
                <NumberInput
                  className="automated billion-input stop"
                  disabled={!row['stopType'] || row['stopType'] === "None" || row['stopType']?.startsWith('Occupancy')}
                  disabledText="N/A"
                  field={[row['i'], 'stop']}
                  min={0}
                  max={row['stopType'] === 'Est. Base Yr' ? 100 : 1000000000}
                  readPrecision={4}
                  writePrecision={4}
                  {...params}
                />
              </div>
              <div>
              { i === 0 && <label>Report Tag</label> }
                <DropdownInput
                  className="automated reporttag"
                  field={[row['i'], 'reportTagNamedItemId']}
                  options={reportTagOptions}
                  onClear={() => updateData(null, [row['i'], 'reportTagNamedItemId'])}
                  {...params}
                />
              </div>
              <div>
                { i === 0 && <label>Cap</label> }
                <CapButton_s
                  hasCap={row['cap']}
                  className={`automated automation_hook_S${i}`}
                  onClick={(e)=>toggleAndShowCap(e, row)}
                  onFocus={(() => {
                    if(showCap) automationService.broadcast("OFFSET_FOCUS_INDEX", -5)
                    setShowCap(false)
                  })}>
                  {row['cap'] ? 'Edit' : 'Create'}
                </CapButton_s>
              </div>
              <RowAddRemove emptyRecord={emptyItem} setState={updateItems} row={i} items={renderItems} />
            </MultiRowCarouselState_s>
        ))}
        </div>
        { showCap && <Cap_s>
          <CapButtons_s>
            <H4>{`Cap | ${renderItems[capItem]['recoveryPoolNamedItemId'] ? _.find(props.namedItems['recoveryPools'], {recoverypoolid: renderItems[capItem]['recoveryPoolNamedItemId']})['code'] : `Row ${capItem + 1}`}`}</H4>
            <div>
              <Button_s danger onClick={()=>clearCap()}>Clear</Button_s>
              <a onClick={()=>setShowCap(false)}><Isvg src={CloseIcon} /></a>
            </div>
          </CapButtons_s>
          <SingleRowCarouselState_s>
            <div>
              <label>Cap</label>
              <ListboxInput
                className="automated"
                hook={`automation_hook_T${capItem}`}
                field="capOn"
                multiple={false}
                noFilter={true}
                options={['Pool', 'Recovery'].map(option => { return {label: option, value: option} })}
                {...capParams(capItem)}
              />
            </div>
            <div>
              <label>Cap Type</label>
                <DropdownInput
                  className={`automated automation_hook_T${capItem}`}
                  field="capType"
                  options={['Percent', '/Area/Yr', '/Yr', 'Prior Yr', 'Detailed /Yr', 'Detailed /Area/Yr']}
                  {...capParams(capItem)}
                />
              </div>
              <div>
                <label>Amt</label>
                <NumberInput
                  className={`automated billion-input automation_hook_T${capItem}`}
                  field="value"
                  disabled={items[capItem]['cap']?.['capType']?.startsWith('Detailed')}
                  disabledText="N/A"
                  min={0}
                  max={1000000000}
                  readPrecision={4}
                  writePrecision={4}
                  {...capParams(capItem)}
                />
              </div>
              <div>
                <label>% Increase</label>
                <NumberInput
                  className={`automated short-input automation_hook_T${capItem}`}
                  disabled={items[capItem]['cap']?.['capType'] === 'Percent' || items[capItem]['cap']?.['capType']?.startsWith('Detailed')}
                  disabledText="N/A"
                  field="increasePercent"
                  min={0}
                  max={100}
                  readPrecision={4}
                  writePrecision={4}
                  onKeyDown={(e) => {
                    if(e.key === 'Tab' && !e.shiftKey && items[capItem]['cap']?.['capType'] !== 'Detailed/Yr' && items[capItem]['cap']?.['capType'] !== 'Detailed Area/Yr') {
                      setShowCap(false)
                      automationService.broadcast("OFFSET_FOCUS_INDEX", -5)
                      if(capItem === renderItems.length - 1) automationService.broadcast("CAROUSEL_TAB_OUT", {})
                    }
                  }}
                  {...capParams(capItem)}
                />
              </div>
          </SingleRowCarouselState_s>
          { renderItems[capItem]['cap']?.['capType']?.startsWith('Detailed') && <SectionFormWell>
              <H4>Details</H4>
              <hr />
              <div className="cap-details">
              { renderCapItems.map((row:object, i) => (
                <MultiRowCarouselState_s className="carousel-multi-row">
                  <div>
                    { i === 0 && <label>Date</label> }
                    <DateInput
                      className={`automated automation_hook_T${capItem}`}
                      field={[`${row['i']}`, 'date']}
                      allowRelative
                      withoutDays
                      {...capItemParams(i)}
                    />
                  </div>
                  <div>
                    { i === 0 && <label>Amt</label> }
                    <NumberInput
                      className={`automated automation_hook_T${capItem}`}
                      field={[`${row['i']}`, 'amt']}
                      min={0}
                      max={2000000000}
                      readPrecision={2}
                      writePrecision={2}
                      onKeyDown={(e) => {
                        if(i === (renderCapItems.length - 1) && e.key === 'Tab' && !e.shiftKey) {
                          setShowCap(false)
                          automationService.broadcast("OFFSET_FOCUS_INDEX", (-3 - renderCapItems.length - 1))
                          if(capItem === renderItems.length - 1) automationService.broadcast("CAROUSEL_TAB_OUT", {})
                        }
                      }}
                      {...capItemParams(i)}
                    />
                  </div>
                  <RowAddRemove emptyRecord={{date: null, amt: null}} setState={updateCapItems} row={i} items={capItems} />
                </MultiRowCarouselState_s>
              ))}
              </div>
              {!homogenous && <FormError_s>All dates must be either relative or absolute</FormError_s>}
            </SectionFormWell>}
        </Cap_s> }
    </div>
  )
}

export default RecoveriesProRata
